import React, { useEffect, useState, useContext, useRef } from 'react';
import Scrollbar from 'perfect-scrollbar';
import { useTranslation } from 'react-i18next';
import { FixedBottom } from 'react-fixed-bottom';
import isEmpty from 'lodash/isEmpty';
import Button from 'components/UI/Antd/Button/Button';
import TextArea from 'components/UI/Antd/TextArea/TextArea';
import MessagesList from './MessagesList';
import useWindowSize from 'library/hooks/useWindowSize';
import { UploadOutlined } from '@ant-design/icons';
import firebaseService from 'services/firebaseService.js';
import messagesService from 'services/messagesService';

import {
  Wrapper,
  MessagesScreen,
  MessagesContainer,
  MessageForm
} from './Messaging.style';
import { AuthContext } from 'context/AuthProvider';
import useAmplitude from 'library/hooks/useAmplitude';
import { getFormattedReservationEvent } from 'library/helpers/amplitude';
import { useSessionStorage } from 'library/hooks/useSessionStorage';
import ReactPixel from 'services/facebookPixel';
import moment from 'moment';
import { message as alertMessage } from 'antd';

alertMessage.config({
  top: 100,
  duration: 7,
  maxCount: 1
});

const Messaging = ({
  className,
  isAllowed,
  isReservation,
  sendMessage,
  messages,
  unread,
  read,
  marina,
  user,
  location,
  reservation,
  isMissingRequirements = false
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { width } = useWindowSize();
  const [message, setMessage] = useState('');
  const { getStatisticsForBoater } = useContext(AuthContext);
  const { sendToAmplitude } = useAmplitude();
  const [numberOfMarinasViewed, setNumberOfMarinasViewed] = useSessionStorage(
    'numberOfMarinasViewed',
    []
  );
  const inputRef = useRef(null);

  useEffect(() => {
    const cont = document.getElementById('scrollContainer');
    let ps = new Scrollbar(cont);
    setTimeout(() => {
      const content = document.getElementById('scrollContent');
      if (content) {
        const contentHeight = content.offsetHeight;
        cont.scrollTop = contentHeight;
      }
    }, 0);

    return () => {
      ps && ps.destroy();
      ps = null;
    };
  }, [messages]);

  const handleSend = () => {
    if (message.trim().length && isAllowed) {
      const data = { text: message };
      sendMessage(data);
      try {
        ReactPixel.trackCustom('Message Marina'); // For tracking custom events. More info about custom events: https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#custom-events
      } catch (e) {
        console.error(e);
      }
    }

    //Amplitude event for update reservation of type = chat.
    if (isReservation && !isEmpty(reservation) && !isEmpty(user)) {
      getStatisticsForBoater().then(res => {
        const boaterProperties = { boater: user, statistics: res };
        const reservationVessel = user.vessels.filter(
          ({ id }) => id === reservation.vessel_id
        )[0];

        const today = moment().startOf('day');
        const days_before = moment(reservation.departure_date).diff(
          today,
          'days'
        );

        const eventProperties = getFormattedReservationEvent({
          location,
          payment: reservation.payment,
          user,
          reservation,
          marina,
          type: 'chat',
          days_before,
          marinas_viewed_before: numberOfMarinasViewed.length - 1,
          reservationVessel,
          popeye_discount: reservation.popeye_discount
        });

        sendToAmplitude({
          boaterProperties,
          eventProperties,
          eventName: 'reservation_updated'
        });
        setNumberOfMarinasViewed([]);
      });
    }
    setMessage('');
  };

  const handleClick = () => {
    inputRef.current.click();
  };

  const onSuccess = () => {
    alertMessage.success(t('CHAT_ATTACHMENT_SUCCESS_UPLOAD_MESSAGE'));
  };
  const onError = () => {
    alertMessage.error(t('CHAT_ATTACHMENT_UPLOAD_ERROR'));
  };
  const onProgress = () => {
    alertMessage.info(t('CHAT_ATTACHMENT_UPLOAD_MESSAGE'));
  };

  const handleFileChange = event => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }

    const filesize = ((fileObj.size/1024)/1024).toFixed(4);

    if (filesize > 20) {
      alertMessage.error(t('CHAT_ATTACHMENT_MAX_SIZE_ERROR'));
      return;
    }

    event.target.value = null;
    setIsLoading(true);

    firebaseService.uploadFile(
      fileObj,
      ({ status, file_path, file_type, name, ...args }) => {
        const data = {
          attachment_type: 'message_attachment',
          name,
          file_path,
          file_type,
          marina_id: marina.id,
          reservation_id: reservation?.id
        };

        if (status === 'PROCESSING') {
          onProgress();
        } else if (status === 'DONE') {

          messagesService.createAttachmentMessage(data).then(res => {
            if (res && res.error_type) {
              onError();
            } else {
              onSuccess(res);
            }
            setIsLoading(false);
          });

        } else if (status === 'ERROR') {
          onError();
        }
      }
    );

  };

  const renderMessagesFrame = () => {
    return (
      <Wrapper className={className}>
        <MessagesScreen className={className}>
          <MessagesContainer id='scrollContainer'>
            <div id='scrollContent'>
              <MessagesList
                isAllowed={isAllowed}
                isReservation={isReservation}
                marina={marina}
                messagesAndStatuses={messages}
                read={read}
                unread={unread}
                isMissingRequirements={isMissingRequirements}
                reservation={reservation}
              />
            </div>
          </MessagesContainer>
        </MessagesScreen>
        <MessageForm className={className}>
          <TextArea
            disabled={!isAllowed}
            className='message-input'
            value={message}
            placeholder={t('TYPE_HERE_PLACEHOLDER')}
            autoSize={{ minRows: 1, maxRows: width > 767 ? 4 : 1 }}
            onChange={e => setMessage(e.target.value)}
          />
          <input style={{display: 'none'}} ref={inputRef} type="file" onChange={handleFileChange} />

          <Button type="primary" shape="round" icon={<UploadOutlined />} className='attach-button' onClick={handleClick}/>
          <Button
            disabled={!message || !isAllowed}
            className='send-button'
            htmlType='button'
            type='primary'
            onClick={handleSend}
          >
            {t('SEND')}
          </Button>
        </MessageForm>
      </Wrapper>
    );
  };

  if (width > 767) {
    return renderMessagesFrame();
  } else {
    return <FixedBottom offset={52}>{renderMessagesFrame()}</FixedBottom>;
  }
};

export default Messaging;
